<template>
  <div class="pricing-group-card mb-3" :class="{ collapsed: collapsedView }">
    <!-- Card Header - Always visible -->
    <div
      class="pricing-group-header d-flex justify-space-between align-center px-2 py-2"
      @click="!collapsedView && toggleExpand()"
    >
      <workpackage-pg-summary
        :unit="unit"
        :is-selectable="isSelectable"
        :prevent-unit-selection="preventUnitSelection"
        :collapsed-view="collapsedView"
      />

      <div v-if="!collapsedView" class="d-flex align-center">
        <v-checkbox
          v-if="isSelectable"
          :value="hasSelectedAll"
          class="my-0 mr-1 pa-0"
          hide-details
          @change="selectAllCategories()"
        />

        <v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </div>
    </div>

    <!-- Expandable Content -->
    <v-expand-transition>
      <div v-if="!collapsedView && isExpanded" class="pricing-group-content">
        <div v-if="unitCategories.length" class="d-flex flex-wrap">
          <div
            v-for="(category, catIndex) in unitCategories"
            :key="catIndex"
            class="category-item pa-3 d-flex justify-space-between align-center"
          >
            <div>{{ category.levelEntryDescription }}</div>
            <div class="d-flex align-center">
              <!-- Checkbox for category selection -->
              <v-checkbox
                v-if="isSelectable"
                v-model="localCategories"
                :value="category.levelEntryKey"
                class="ma-0 pa-0"
                hide-details
                @change="updateCategories"
              />

              <!-- Approval indicators -->
              <div v-if="!isSelectable" class="d-flex">
                <approval-icon-new
                  :tooltip-message="$t('gridView.tooltips.categoryManagerApproval')"
                  :approved="isCategoryApproved(category, 'categoryManagerApproval')"
                  class="pr-1"
                />
                <approval-icon-new
                  :tooltip-message="$t('gridView.tooltips.pricingSpecialistApproval')"
                  :approved="isCategoryApproved(category, 'pricingSpecialistApproval')"
                />
              </div>
            </div>
          </div>
        </div>
        <div v-else class="pa-3 text-center grey--text">
          No categories available
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { get, size, every, cloneDeep, isEmpty, some, forEach } from 'lodash';

export default {
  name: 'WorkpackagePgItem',

  props: {
    unit: {
      type: Object,
      required: true,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    isSelectable: {
      type: Boolean,
      default: false,
    },
    preventUnitSelection: {
      type: Boolean,
      default: true,
    },
    collapsedView: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localCategories: [],
      hasSelectedAll: false,
      isExpanded: false,
    };
  },

  computed: {
    ...mapGetters('hierarchy', ['getHierarchyForWorkpackage']),

    unitCategories() {
      return this.getHierarchyForWorkpackage(
        {
          level: this.unit.level + 1,
          parentId: this.unit.levelEntryKey,
        },
        this.unit.workpackageId
      );
    },

    numberOfCategories() {
      return size(this.unitCategories);
    },
  },

  watch: {
    unit() {
      this.localCategories = [];
      this.hasSelectedAll = false;
    },

    // Sync external categories prop to local state
    categories: {
      immediate: true,
      handler(newCategories) {
        this.localCategories = cloneDeep(newCategories);
        // Update hasSelectedAll status based on selection count
        if (this.unitCategories && this.unitCategories.length > 0) {
          this.hasSelectedAll = this.localCategories.length === this.unitCategories.length;
        }
      },
    },
  },

  methods: {
    ...mapActions('workpackages', ['getPriceChangeData', 'setUserSelectionUnit']),

    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },

    updateCategories() {
      this.hasSelectedAll = this.localCategories.length === this.unitCategories.length;
      this.$emit('categories-updated', this.unit._id, this.localCategories);
    },

    selectAllCategories() {
      this.hasSelectedAll = !this.hasSelectedAll;
      const categories = [];

      if (this.hasSelectedAll) {
        forEach(this.unitCategories, category => {
          categories.push(category.levelEntryKey);
        });
      }

      this.localCategories = categories;
      this.$emit('categories-updated', this.unit._id, categories);
    },

    isCategoryApproved(category, approvalType) {
      const pricingGroups = this.getHierarchyForWorkpackage(
        {
          parentId: category.levelEntryKey,
        },
        this.unit.workpackageId
      );

      if (isEmpty(pricingGroups)) return null;

      const isApproved = every(
        pricingGroups,
        pg => get(pg, `approvalStatus.${approvalType}.approved`, null) === true
      );

      const hasRejections = some(
        pricingGroups,
        pg => get(pg, `approvalStatus.${approvalType}.approved`, null) === false
      );

      const isNull = every(
        pricingGroups,
        pg => get(pg, `approvalStatus.${approvalType}.approved`, null) === null
      );

      if (isNull) return null;
      if (hasRejections) return false;
      if (isApproved) return true;
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
.pricing-group-card {
  border-radius: 4px;
  overflow: hidden;

  &.collapsed {
    width: 184px;
    margin-bottom: 0px !important;
  }
}

.pricing-group-header {
  background-color: inherit;
  cursor: pointer;
}

.pricing-group-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #023b6e;

  &:hover {
    cursor: pointer;
  }
}

.pricing-group-content {
  background-color: inherit;
  border-top: 1px solid #a2a9b0;
}

.category-item {
  width: 20%;
  color: #343a3f;
  font-size: 1.2rem;

  @media (max-width: 1200px) {
    width: 33.33%;
  }

  @media (max-width: 960px) {
    width: 50%;
  }
}

/* Hide checkbox details to maintain clean look */
:deep(.v-input--selection-controls__input) {
  margin-right: 0;
}

:deep(.v-messages) {
  display: none;
}
</style>
